import type { ExpertsSearchResponse } from "../types";
import {
  citedByText,
  countFormula,
  isEmerging,
  optionsOf,
  rankingFactors,
  scoreFormula,
  shareFormula,
  weightFormula,
  yearsOnTopic,
} from "./factors";
import { paperUrl, profileUrl } from "./links";

/** The ranked people with their papers and evidence, as markdown to paste anywhere. */
export const expertsMarkdown = (d: ExpertsSearchResponse): string => {
  const c = d.counts;
  const factors = rankingFactors(d);
  const count = countFormula(optionsOf(d), d.recent_since ?? new Date().getFullYear() - 3);
  const head = [
    `# Experts: ${d.topic}`,
    `- Retrieved ${new Date().toLocaleDateString("en-CA")} from GXL Paperclip experts search (staff preview)`,
    `- Read ${c.candidates} papers (${c.relevant} on topic, ${c.reviews} reviews); ${c.foundational} foundational papers: papers search missed that the on-topic papers cite most`,
  ].join("\n");
  const g = d.topic_guide;
  const scoring = [
    "## How people were ranked",
    g
      ? `**Topic read as:** ${g.description}` +
        (g.excludes ? ` Not counted: ${g.excludes.replace(/\.$/, "")}.` : "")
      : null,
    isEmerging(d) ? "**Emerging researchers:** first authors only and recent work." : null,
    count
      ? `**${count}.** A paper counts when P(on-topic) is at least ${d.min_relevance ?? 0.5}.`
      : `Paper weight = ${weightFormula(factors)}. Authorship credit = ${shareFormula(optionsOf(d))}. Score = ${scoreFormula(optionsOf(d))}.`,
    count
      ? null
      : factors
          .map((x) => `- **${x.name}**${x.option ? ` (${x.on ? "on" : "off"})` : ""}: ${x.text}`)
          .join("\n"),
  ]
    .filter(Boolean)
    .join("\n\n");
  const people = d.experts.map((e, i) => {
    const facts = [
      e.affiliation,
      `${e.papers} papers on the topic (${e.lead_papers} as first or last author)`,
      yearsOnTopic(e, d.recent_since),
      `${e.citations.toLocaleString("en-US")} citations`,
      e.h_index != null ? `h-index ${e.h_index}` : null,
      `score ${e.score.toFixed(1)}`,
      profileUrl(e),
    ].filter(Boolean);
    const papers = e.evidence.map((ev) => {
      const p = ev.paper;
      const meta = [
        `${ev.author_position} author`,
        p.year,
        p.journal,
        p.citations != null ? `${p.citations} citations` : null,
        p.cited_by_candidates
          ? `${p.foundational ? "foundational: " : ""}${citedByText(p.cited_by_candidates)}`
          : null,
        (ev.review ?? 0) >= 0.5 ? "review" : null,
      ].filter(Boolean);
      const url = paperUrl(p);
      const line = `- ${url ? `[${p.title}](${url})` : p.title} (${meta.join(", ")})`;
      return ev.evidence ? `${line}\n  > ${ev.evidence}` : line;
    });
    return [`## ${i + 1}. ${e.name}`, facts.join(" · "), ...papers].join("\n\n");
  });
  return [head, scoring, ...people].join("\n\n") + "\n";
};
